import { useMemo, useState } from 'react';
import {
  CalendarCheck,
  CalendarOff,
  ChevronLeft,
  ChevronRight,
  Clock,
  IndianRupee,
  Loader2,
  MapPin,
  PawPrint,
  Phone,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { SectionHeader } from '@/components/ui/section-header';
import { SelectField } from '@/components/ui/select';
import { StatusPill, type StatusTone } from '@/components/ui/status-pill';
import { ErrorState } from '@/components/ui/error-state';
import { Skeleton } from '@/components/ui/skeleton';
import { use_draft } from '@/features/onboarding/hooks';
import { use_schedule_day, use_schedule_month } from '@/features/schedule/hooks';
import type { BookingStatus, DayTone, ScheduleDay } from '@/features/schedule/api';
import type { VendorModuleType } from '@/features/availability/types';

const IST = 'Asia/Kolkata';
const WEEKDAY_INITIALS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const today_ist = () => new Date().toLocaleDateString('en-CA', { timeZone: IST });
const current_month = () => today_ist().slice(0, 7);

function shift_month(month: string, by: number): string {
  const [y, m] = month.split('-').map(Number);
  // Date's month arithmetic handles the year rollover, which manual modulo gets wrong at December.
  const d = new Date(Date.UTC(y, m - 1 + by, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

function month_label(month: string): string {
  const [y, m] = month.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-IN', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function day_label(date: string): string {
  return new Date(`${date}T00:00:00Z`).toLocaleDateString('en-IN', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

const time_of = (iso: string | null) =>
  iso ? new Date(iso).toLocaleTimeString('en-IN', { timeZone: IST, hour: '2-digit', minute: '2-digit' }) : '—';

/**
 * How a cell reads at a glance. The server decides the tone — a blackout outranks bookings,
 * because a day the vendor closed with work still on it is the cell they most need to notice.
 */
/**
 * `07:00` -> `7a`. The cell has room for roughly eight characters including the dash, so the
 * minutes are dropped when they are zero — which is almost always, since vendors set hours on the
 * hour. A half-hour open stays as `7:30a` rather than silently rounding to something untrue.
 */
function short_time(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number);
  const suffix = h < 12 ? 'a' : 'p';
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return m === 0 ? `${hour12}${suffix}` : `${hour12}:${String(m).padStart(2, '0')}${suffix}`;
}

/** 1600 -> `1.6k`. Full rupees fit in the totals row; a cell has to abbreviate. */
function compact_money(amount: number): string {
  if (amount >= 100000) return `${(amount / 100000).toFixed(1)}L`;
  if (amount >= 1000) return `${(amount / 1000).toFixed(1)}k`;
  return String(Math.round(amount));
}

const TONE_CLASS: Record<DayTone, string> = {
  blackout: 'border-destructive/40 bg-destructive/10',
  booked: 'border-primary/40 bg-primary/10',
  open: 'border-emerald-500/30 bg-emerald-500/5',
  closed: 'border-border bg-muted/30',
};

const STATUS_TONE: Record<BookingStatus, StatusTone> = {
  PENDING_PAYMENT: 'neutral',
  AWAITING_VENDOR: 'warning',
  CONFIRMED: 'info',
  IN_PROGRESS: 'info',
  COMPLETED: 'success',
  CANCELLED: 'neutral',
  NO_SHOW: 'destructive',
};

const STATUS_LABEL: Record<BookingStatus, string> = {
  PENDING_PAYMENT: 'Unpaid',
  AWAITING_VENDOR: 'Needs your reply',
  CONFIRMED: 'Confirmed',
  IN_PROGRESS: 'In progress',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
  NO_SHOW: 'No-show',
};

/**
 * The vendor's schedule.
 *
 * There was nowhere to see a day. Bookings lived in a flat inbox and availability was a set of
 * abstract weekday rules, so "what does my Thursday look like" and "what happened last Tuesday"
 * were both unanswerable. This is the calendar over the top of both, and it goes as far back as
 * the vendor has history.
 */
export function VendorSchedulePage() {
  const draft_q = use_draft();
  const [month, set_month] = useState(current_month());
  const [module_type, set_module_type] = useState<VendorModuleType | ''>('');
  const [selected, set_selected] = useState<string | null>(today_ist());

  const month_q = use_schedule_month(month, module_type);
  const day_q = use_schedule_day(selected, module_type);

  const modules = (draft_q.data?.module_types ?? []) as VendorModuleType[];

  /**
   * Leading blanks so the 1st lands under its real weekday.
   *
   * Taken from the first day's own `weekday` rather than recomputed from the month string — the
   * server buckets in IST, and a second local computation is a chance to disagree with it.
   */
  const leading_blanks = useMemo(() => {
    const first = month_q.data?.days[0];
    return first ? first.weekday : 0;
  }, [month_q.data]);

  const is_current = month === current_month();

  return (
    <div className="flex flex-col gap-6" data-testid="vendor-schedule">
      <SectionHeader
        title="Schedule"
        description="Your month at a glance — bookings, earnings, open days and closures. Past months included."
      />

      <Card>
        <CardContent className="flex flex-col gap-4 p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => set_month((m) => shift_month(m, -1))}
                aria-label="Previous month"
                data-testid="schedule-prev-month"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="min-w-[10rem] text-center font-heading text-base font-semibold" data-testid="schedule-month-label">
                {month_label(month)}
              </span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => set_month((m) => shift_month(m, 1))}
                aria-label="Next month"
                data-testid="schedule-next-month"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              {!is_current && (
                <Button variant="ghost" size="sm" onClick={() => set_month(current_month())} data-testid="schedule-today">
                  Today
                </Button>
              )}
            </div>

            {modules.length > 1 && (
              <div className="w-52">
                <SelectField
                  value={module_type}
                  onValueChange={(v) => set_module_type(v as VendorModuleType | '')}
                  options={[
                    { value: '', label: 'All modules' },
                    ...modules.map((m) => ({ value: m, label: m })),
                  ]}
                  aria-label="Filter by module"
                  data-testid="schedule-module-filter"
                />
              </div>
            )}
          </div>

          {month_q.isLoading ? (
            <Skeleton className="h-72 rounded-card" />
          ) : month_q.isError ? (
            <ErrorState
              title="Couldn't load your schedule"
              description="Please try again."
              action={
                <Button variant="secondary" size="sm" onClick={() => month_q.refetch()}>
                  Retry
                </Button>
              }
            />
          ) : (
            <>
              {/* Month totals. Earnings are take-home from completed work only. */}
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="schedule-totals">
                <Totals label="Bookings" value={String(month_q.data?.totals.booking_count ?? 0)} />
                <Totals label="Completed" value={String(month_q.data?.totals.completed ?? 0)} />
                <Totals label="Earned" value={`₹${month_q.data?.totals.earnings_inr ?? '0.00'}`} hint="After platform fee, completed only" />
                <Totals label="Days closed" value={String(month_q.data?.totals.blackout_days ?? 0)} />
              </div>

              <div className="grid grid-cols-7 gap-1 text-center text-xs font-semibold text-muted-foreground">
                {WEEKDAY_INITIALS.map((w, i) => (
                  <span key={i}>{w}</span>
                ))}
              </div>

              <div className="grid grid-cols-7 gap-1" data-testid="schedule-grid">
                {Array.from({ length: leading_blanks }, (_, i) => (
                  <div key={`blank-${i}`} aria-hidden />
                ))}
                {(month_q.data?.days ?? []).map((d) => (
                  <DayCell
                    key={d.date}
                    day={d}
                    selected={selected === d.date}
                    // Only worth the space when the vendor actually runs more than one, and not
                    // when they have already filtered down to a single module.
                    show_modules={modules.length > 1 && module_type === ''}
                    on_select={() => set_selected(d.date)}
                  />
                ))}
              </div>

              <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                <Legend tone="booked" label="Has bookings" />
                <Legend tone="open" label="Open, nothing booked" />
                <Legend tone="closed" label="No hours set" />
                <Legend tone="blackout" label="Closed (exception)" />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Day detail */}
      {selected && (
        <Card data-testid="schedule-day-panel">
          <CardContent className="flex flex-col gap-4 p-5">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h2 className="font-heading text-base font-semibold" data-testid="schedule-day-title">
                {day_label(selected)}
              </h2>
              {day_q.data?.is_past && <StatusPill tone="neutral">Past</StatusPill>}
              {day_q.data?.is_today && <StatusPill tone="info">Today</StatusPill>}
            </div>

            {day_q.isLoading ? (
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            ) : day_q.isError ? (
              <ErrorState title="Couldn't load this day" description="Please try again." />
            ) : (
              <>
                {(day_q.data?.blackouts.length ?? 0) > 0 && (
                  <div
                    className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm"
                    data-testid="schedule-day-blackout"
                  >
                    <CalendarOff className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                    <span>
                      Closed on this date
                      {day_q.data?.blackouts[0]?.reason ? ` — ${day_q.data.blackouts[0].reason}` : ''}.
                      {(day_q.data?.bookings.length ?? 0) > 0 &&
                        ' You still have bookings on it — these need moving or cancelling.'}
                    </span>
                  </div>
                )}

                {/* The hours that were open, and which rule opened them. */}
                <div className="flex flex-col gap-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Available hours
                  </p>
                  {(day_q.data?.windows.length ?? 0) === 0 ? (
                    <p className="text-sm text-muted-foreground" data-testid="schedule-day-no-windows">
                      No hours set for this date.
                    </p>
                  ) : (
                    <div className="flex flex-wrap gap-2" data-testid="schedule-day-windows">
                      {day_q.data?.windows.map((w) => (
                        <span
                          key={w.id}
                          className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/40 px-2.5 py-1 text-xs"
                        >
                          <Clock className="h-3 w-3" />
                          {w.start_time}–{w.end_time}
                          <span className="text-muted-foreground">· {w.capacity} at a time</span>
                          {/* Says whether to edit the weekly pattern or delete a one-off. */}
                          <span className="text-muted-foreground">· {w.source === 'date' ? 'this date only' : 'weekly'}</span>
                          {day_q.data && day_q.data.modules.length > 1 && (
                            <span className="text-muted-foreground">· {w.module_type}</span>
                          )}
                        </span>
                      ))}
                    </div>
                  )}
                  {(day_q.data?.blocked_slots.length ?? 0) > 0 && (
                    <p className="text-xs text-muted-foreground" data-testid="schedule-day-blocked">
                      {day_q.data?.blocked_slots.length} individual slot
                      {(day_q.data?.blocked_slots.length ?? 0) === 1 ? '' : 's'} blocked:{' '}
                      {day_q.data?.blocked_slots.map((b) => time_of(b.slot_start)).join(', ')}
                    </p>
                  )}
                </div>

                {/* Bookings */}
                <div className="flex flex-col gap-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Bookings ({day_q.data?.bookings.length ?? 0})
                  </p>
                  {(day_q.data?.bookings.length ?? 0) === 0 ? (
                    <p className="text-sm text-muted-foreground" data-testid="schedule-day-no-bookings">
                      Nothing booked on this date.
                    </p>
                  ) : (
                    <div className="flex flex-col gap-2" data-testid="schedule-day-bookings">
                      {day_q.data?.bookings.map((b) => (
                        <div
                          key={b.id}
                          className="flex flex-col gap-2 rounded-lg border border-border p-3"
                          data-testid={`schedule-booking-${b.id}`}
                        >
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <span className="font-mono text-sm font-semibold">
                              {time_of(b.slot_start)} – {time_of(b.slot_end)}
                            </span>
                            <StatusPill tone={STATUS_TONE[b.status]}>{STATUS_LABEL[b.status]}</StatusPill>
                          </div>
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                            <span>{b.service?.name ?? 'Service'}</span>
                            {b.pet && (
                              <span className="inline-flex items-center gap-1 text-muted-foreground">
                                <PawPrint className="h-3.5 w-3.5" /> {b.pet.name} ({b.pet.species})
                              </span>
                            )}
                            {b.customer && (
                              <>
                                <span className="text-muted-foreground">{b.customer.name ?? 'Customer'}</span>
                                {/* The vendor is going to their address; they must be able to call. */}
                                <a
                                  href={`tel:${b.customer.phone}`}
                                  className="inline-flex items-center gap-1 text-primary hover:underline"
                                >
                                  <Phone className="h-3.5 w-3.5" /> {b.customer.phone}
                                </a>
                              </>
                            )}
                          </div>
                          {b.pickup_address && (
                            <span className="inline-flex items-start gap-1 text-xs text-muted-foreground">
                              <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {b.pickup_address}
                            </span>
                          )}
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                            {/*
                              Two numbers, labelled. The vendor is paid the second one — the
                              platform cut is deducted on services, so showing only the price
                              would have them expect the whole thing.
                            */}
                            <span className="text-muted-foreground">Customer paid ₹{b.price_inr}</span>
                            <span className="inline-flex items-center gap-1 font-semibold">
                              <IndianRupee className="h-3 w-3" /> You earn ₹{b.vendor_earnings_inr}
                            </span>
                            {b.refunded_inr && Number(b.refunded_inr) > 0 && (
                              <span className="text-destructive">Refunded ₹{b.refunded_inr}</span>
                            )}
                          </div>
                          {b.vendor_notes && (
                            <p className="rounded-md bg-muted/40 px-2 py-1 text-xs">Note: {b.vendor_notes}</p>
                          )}
                          {b.cancel_reason && (
                            <p className="text-xs text-muted-foreground">
                              Cancelled{b.cancelled_by ? ` by ${b.cancelled_by.toLowerCase()}` : ''} — {b.cancel_reason}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Totals({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-lg border border-border p-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 font-heading text-lg font-semibold">{value}</p>
      {hint && <p className="text-[0.7rem] leading-tight text-muted-foreground">{hint}</p>}
    </div>
  );
}

function Legend({ tone, label }: { tone: DayTone; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`h-3 w-3 rounded border ${TONE_CLASS[tone]}`} />
      {label}
    </span>
  );
}

/**
 * A calendar cell.
 *
 * The tone alone was not enough. An open day with nothing booked rendered as an empty green box,
 * which told the vendor the day existed and nothing else — so most of a month was dead space. Each
 * cell now carries the day's actual shape: when it opens, how much of it is committed, and what it
 * earned. Compact badges rather than sentences, because there are 31 of these on screen at once.
 *
 * Order is by urgency, not by data availability: something needing a reply outranks a count, which
 * outranks money already banked. Whatever is dropped for space is dropped from the bottom.
 */
function DayCell({
  day,
  selected,
  show_modules,
  on_select,
}: {
  day: ScheduleDay;
  selected: boolean;
  /** Only for a multi-module vendor — a single-module one already knows which module it is. */
  show_modules: boolean;
  on_select: () => void;
}) {
  const day_number = Number(day.date.slice(-2));
  const awaiting = day.counts.AWAITING_VENDOR ?? 0;
  const completed = day.counts.COMPLETED ?? 0;
  const no_show = day.counts.NO_SHOW ?? 0;
  const earned = Number(day.earnings_inr);

  /*
   * A screen-reader label built from the same fields as the badges. The badges are three-character
   * abbreviations chosen to fit; read aloud they would be gibberish, so the whole cell gets one
   * plain-language summary and the badges are hidden from the accessibility tree.
   */
  const spoken = [
    day_label(day.date),
    day.is_blackout ? 'closed' : day.hours ? `open ${day.hours.opens_at} to ${day.hours.closes_at}` : 'no hours set',
    day.booking_count > 0 ? `${day.booking_count} booking${day.booking_count === 1 ? '' : 's'}` : 'nothing booked',
    awaiting > 0 ? `${awaiting} awaiting your reply` : '',
    earned > 0 ? `earned ${earned} rupees` : '',
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <button
      type="button"
      onClick={on_select}
      aria-pressed={selected}
      aria-label={spoken}
      data-testid={`schedule-day-${day.date}`}
      data-tone={day.tone}
      className={[
        'group relative flex min-h-[5.75rem] flex-col items-stretch gap-1 overflow-hidden rounded-lg border p-1.5 text-left',
        'transition-all duration-150 hover:-translate-y-px hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
        TONE_CLASS[day.tone],
        selected ? 'ring-2 ring-primary' : '',
        // Past days are dimmed, not hidden — they are the whole reason the history exists.
        day.is_past ? 'opacity-75' : '',
      ].join(' ')}
    >
      {/* Date, and the day's hours on the same line — the two things every cell has. */}
      <span className="flex items-baseline justify-between gap-1" aria-hidden>
        <span
          className={
            day.is_today
              ? 'inline-flex h-[1.15rem] min-w-[1.15rem] items-center justify-center rounded-full bg-primary px-1 text-[0.7rem] font-bold text-primary-foreground'
              : 'text-[0.8rem] font-semibold'
          }
        >
          {day_number}
        </span>
        {day.hours && !day.is_blackout && (
          <span className="truncate text-[0.6rem] font-medium tabular-nums text-muted-foreground">
            {short_time(day.hours.opens_at)}–{short_time(day.hours.closes_at)}
            {/* A second window means the range hides a gap. Saying so beats implying one block. */}
            {day.hours.window_count > 1 && <span className="ml-0.5">+{day.hours.window_count - 1}</span>}
          </span>
        )}
      </span>

      <span className="flex flex-col gap-0.5" aria-hidden>
        {/* Needs action first. The dot pulses because this is the one thing that expires. */}
        {awaiting > 0 && (
          <span className="inline-flex items-center gap-1 rounded bg-amber-500/25 px-1 py-px text-[0.62rem] font-semibold leading-tight text-amber-900 dark:text-amber-200">
            <span className="h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-amber-500" />
            {awaiting} to accept
          </span>
        )}

        {day.is_blackout && (
          <span className="inline-flex items-center gap-1 text-[0.62rem] font-semibold leading-tight text-destructive">
            <CalendarOff className="h-2.5 w-2.5 shrink-0" />
            Closed
          </span>
        )}

        {day.booking_count > 0 && (
          <span className="inline-flex items-center gap-1 rounded bg-primary/25 px-1 py-px text-[0.62rem] font-semibold leading-tight">
            <CalendarCheck className="h-2.5 w-2.5 shrink-0" />
            {day.booking_count}
            {/* Concurrent capacity, so "3/4" reads as how full the day is rather than a bare count. */}
            {day.hours && day.hours.max_concurrent > 0 && (
              <span className="font-normal opacity-70">/{day.hours.max_concurrent} slots</span>
            )}
          </span>
        )}

        {/* Where the day starts. Only useful before it happens. */}
        {day.first_booking_at && !day.is_past && (
          <span className="truncate text-[0.6rem] leading-tight text-muted-foreground">
            from {time_of(day.first_booking_at)}
          </span>
        )}

        {no_show > 0 && (
          <span className="inline-flex items-center gap-1 rounded bg-destructive/20 px-1 py-px text-[0.62rem] font-semibold leading-tight text-destructive">
            {no_show} no-show
          </span>
        )}

        {!day.hours && !day.is_blackout && day.booking_count === 0 && (
          <span className="text-[0.6rem] leading-tight text-muted-foreground/70">No hours</span>
        )}

        {day.blocked_slot_count > 0 && !day.is_blackout && (
          <span className="truncate text-[0.6rem] leading-tight text-muted-foreground">
            {day.blocked_slot_count} slot{day.blocked_slot_count === 1 ? '' : 's'} blocked
          </span>
        )}

        {show_modules && day.hours && day.hours.modules.length > 0 && (
          <span className="flex flex-wrap gap-0.5">
            {day.hours.modules.map((m) => (
              <span
                key={m}
                className="rounded bg-foreground/10 px-1 text-[0.55rem] font-medium uppercase leading-tight tracking-wide"
              >
                {/* Three letters is all that fits, and WAL/TRA/GRO stay distinguishable. */}
                {m.slice(0, 3)}
              </span>
            ))}
          </span>
        )}
      </span>

      {/* Money last and bottom-aligned, so it lines up across the row and never pushes anything out. */}
      {earned > 0 && (
        <span
          className="mt-auto flex items-center gap-0.5 text-[0.62rem] font-semibold leading-tight text-emerald-700 dark:text-emerald-400"
          aria-hidden
        >
          <IndianRupee className="h-2.5 w-2.5 shrink-0" />
          {compact_money(earned)}
          {completed > 0 && <span className="ml-0.5 font-normal opacity-70">· {completed} done</span>}
        </span>
      )}
    </button>
  );
}
